/**
 * Scroll helpers shared by the configure-view scroll machinery.
 *
 * The widget can be embedded two ways:
 *  - fixed height ("kiosk") -> the widget's internal `[data-scroll-container]`
 *    (`.apex-overflow-auto`) is the scrollport.
 *  - auto height (page-flow) -> that container is released to `overflow:visible`
 *    and the HOST PAGE (window) is the scrollport.
 *
 * Scroll-to uses `scrollIntoView` + `scroll-margin-top`, which is scrollport-
 * agnostic. Active-category tracking and the compact-bar observer still need to
 * know which element actually scrolls, which is what these helpers resolve.
 */

/**
 * Read the resolved `--apex-sticky-offset` (px) inherited at `el`. This is the
 * host page's fixed-header height in auto embeds (set via the `sticky-top`
 * attribute). Returns 0 when unset/unparseable.
 */
export function readStickyOffsetPx(el: Element | null): number {
  if (!el) return 0;
  const raw = getComputedStyle(el).getPropertyValue("--apex-sticky-offset").trim();
  const px = parseFloat(raw);
  return Number.isNaN(px) ? 0 : px;
}

/**
 * Whether the internal container is the real scrollport (fixed mode) rather than
 * the page (auto mode). In auto mode the container computes `overflow:visible`.
 */
export function containerIsScroller(container: Element | null): boolean {
  if (!container) return false;
  return getComputedStyle(container).overflowY !== "visible";
}
